// Function-valued demo options stay in JavaScript; ordinary component options remain in HTML.
const slots = (date) => (date.getDate() % 4) + 1
const labels = ["Very dissatisfied", "Dissatisfied", "Neutral", "Satisfied", "Very satisfied"]
export const examples = {
  "disabled-autocomplete": () => ({
    id: "available-framework",
    showHints: true,
    allowOnlyDefinedOptions: true,
    options: [
      { label: "Angular", value: "angular" },
      { label: "Bootstrap", value: "bootstrap", disabled: true },
      { label: "CoreUI", value: "coreui" },
      {
        label: "Backend (unavailable)",
        disabled: true,
        options: [
          { label: "Django", value: "django" },
          { label: "Laravel", value: "laravel" },
        ],
      },
    ],
  }),
  "destination-autocomplete": () => ({
    id: "destination-input",
    name: "destination",
    placeholder: "Search destinations...",
    search: "global",
    searchNoResultsLabel: "No matching destinations",
    allowOnlyDefinedOptions: true,
    options: [
      {
        label: "Australia",
        options: [
          { label: "Perth", value: "PER" },
          { label: "Sydney", value: "SYD" },
        ],
      },
      {
        label: "Asia",
        options: [
          { label: "Singapore", value: "SIN" },
          { label: "Tokyo", value: "HND" },
        ],
      },
    ],
    optionsTemplate: (option) =>
      `<span class="d-flex justify-content-between gap-3"><span>${option.label}</span><span class="text-body-secondary">${option.value}</span></span>`,
  }),
  "project-tags": () => ({
    name: "tags",
    maxChips: 5,
    unique: true,
    selectable: true,
    chipClassName: "chip-primary",
  }),
  "verification-otp": () => ({ name: "verification-code" }),
  "quarter-calendar": () => ({
    locale: "en-US",
    selectionType: "quarter",
    renderQuarterCell(date) {
      const quarter = Math.floor(date.getMonth() / 3)
      return `<span class="d-flex flex-column"><span class="fw-semibold">Q${quarter + 1}</span><small>${["Jan–Mar", "Apr–Jun", "Jul–Sep", "Oct–Dec"][quarter]}</small></span>`
    },
  }),
  "availability-calendar": () => ({
    locale: "en-US",
    disabledDates: (date) => [0, 6].includes(date.getDay()),
    renderDayCell(date, meta) {
      const count = slots(date)
      return `<span class="d-flex flex-column lh-sm"><span>${date.getDate()}</span>${meta.isDisabled || !meta.isInCurrentMonth ? "" : `<small class="text-nowrap">${count} ${count === 1 ? "slot" : "slots"}</small>`}</span>`
    },
  }),
  "team-select": () => ({
    id: "team-members",
    name: "team-members",
    search: true,
    ariaSearchLabel: "Search team members",
    placeholder: "Choose up to three people",
    selectAllMode: "filtered",
    optionsGroupsSelectable: true,
    selectionLimit: 3,
    options: [
      {
        label: "Design",
        options: [
          { value: "alex", text: "Alex Morgan", role: "Product designer" },
          { value: "sam", text: "Sam Rivera", role: "UX researcher" },
        ],
      },
      {
        label: "Engineering",
        options: [
          { value: "jordan", text: "Jordan Lee", role: "Frontend developer" },
          { value: "taylor", text: "Taylor Kim", role: "Backend developer" },
          { value: "casey", text: "Casey Chen", role: "QA engineer" },
        ],
      },
    ],
    optionsTemplate: (option) =>
      `<span class="d-block">${option.text}</span><small class="text-body-secondary">${option.role}</small>`,
    optionsGroupsTemplate: (group) => `<span class="fw-semibold">${group.label}</span>`,
    headerTemplate(state, actions) {
      const header = document.createElement("div")
      header.className = "px-3 py-2 small"
      const summary = document.createElement("p")
      summary.className = "text-body-secondary mb-2"
      summary.textContent = `${state.selected} of 3 selected · ${state.filtered} matching people`
      header.append(summary)
      for (const [label, action, disabled] of [
        ["Select matching", actions.selectFiltered, state.filtered === 0],
        ["Clear selection", actions.deselectAll, state.selected === 0],
      ]) {
        const button = document.createElement("button")
        button.type = "button"
        button.className = "btn btn-sm btn-outline-primary me-2"
        button.textContent = label
        button.disabled = disabled
        button.addEventListener("click", action)
        header.append(button)
      }
      return header
    },
  }),
  "budget-slider": () => ({
    min: 0,
    max: 1000,
    step: 50,
    value: [200, 800],
    ariaLabels: ["Minimum project budget", "Maximum project budget"],
    tooltipsFormat: (value) => `$${value}`,
  }),
  "satisfaction-rating": () => ({
    value: 3,
    ariaLabel: (value, total) => `${labels[value - 1]}, ${value} of ${total} stars`,
  }),
}

const disabledDates = () => ({
  locale: "en-US",
  calendarDate: new Date(2022, 2, 1),
  disabledDates: [
    [new Date(2022, 2, 4), new Date(2022, 2, 7)],
    new Date(2022, 2, 16),
    new Date(2022, 3, 16),
    [new Date(2022, 4, 2), new Date(2022, 4, 8)],
  ],
  maxDate: new Date(2022, 5, 0),
  minDate: new Date(2022, 1, 1),
})
examples.disabledDates = disabledDates
examples.customRanges = () => {
  const today = new Date()
  const ago = (days) => new Date(today.getFullYear(), today.getMonth(), today.getDate() - days)
  return {
    locale: "en-US",
    ranges: {
      Today: [today, today],
      Yesterday: [ago(1), ago(1)],
      "Last 7 Days": [ago(6), today],
      "Last 30 Days": [ago(29), today],
      "This Month": [
        new Date(today.getFullYear(), today.getMonth(), 1),
        new Date(today.getFullYear(), today.getMonth() + 1, 0),
      ],
      "Last Month": [
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        new Date(today.getFullYear(), today.getMonth(), 0),
      ],
    },
  }
}
examples.readonlyTime = () => ({ time: "20:00:15", disabled: false, readonly: true })
examples["remote-team"] = () => ({
  name: "remote-team",
  search: ["external", "global"],
  ariaSearchLabel: "Search directory",
  options: [],
  placeholder: "Search names...",
})
examples["remote-person"] = () => ({
  id: "remote-person-input",
  name: "remote-person",
  search: "external",
  options: [],
  placeholder: "Search names...",
  allowOnlyDefinedOptions: true,
})
